import React, { useEffect } from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Checkbox,
  FormGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  BusinessTemplateType,
  CustomSettings,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import { useUserLanguages } from "../hooks/useUserLanguages";
import { useDefaultLabels } from "../hooks/useDefaultLabels";

interface HeaderSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5, DELIVERY_NOTE = 6
}

const HeaderSettingsPanel: React.FC<HeaderSettingsPanelProps> = ({
  settings,
  onChange,
  templateType = 2,
}) => {
  const { t } = useTranslation();
  const { primaryLanguageName, secondaryLanguageName } = useUserLanguages();
  const { getCompanyLabel, getCustomerLabel } = useDefaultLabels();

  // Initialize default heading values when type changes (only if fields don't exist)
  useEffect(() => {
    if (templateType === 3) {
      // JOBCARD - initialize if not set
      if (settings.jobcardHeadingPrimary === undefined && settings.jobcardHeadingSecondary === undefined) {
        onChange({
          jobcardHeadingPrimary: "JOBCARD",
          jobcardHeadingSecondary: "",
        });
      }
    } else if (templateType === 4) {
      // WORKORDER - initialize if not set
      if (settings.workorderHeadingPrimary === undefined && settings.workorderHeadingSecondary === undefined) {
        onChange({
          workorderHeadingPrimary: "WORKORDER",
          workorderHeadingSecondary: "",
        });
      }
    } else if (templateType === 6) {
      // DELIVERY NOTE - initialize if not set
      if (settings.deliveryNoteHeadingPrimary === undefined && settings.deliveryNoteHeadingSecondary === undefined) {
        onChange({
          deliveryNoteHeadingPrimary: "DELIVERY NOTE",
          deliveryNoteHeadingSecondary: "",
        });
      }
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      if (
        settings.proformaInvoiceHeadingPrimary === undefined &&
        settings.proformaInvoiceHeadingSecondary === undefined
      ) {
        onChange({
          proformaInvoiceHeadingPrimary: "PROFORMA INVOICE",
          proformaInvoiceHeadingSecondary: "",
        });
      }
    }
    // Note: We don't initialize INVOICE here as it's the default and likely already set
  }, [templateType]); // Only run when templateType changes

  const updateCompanyFields = (field: string, value: boolean) => {
    const newCompanyFields = {
      ...settings.companyFields,
      [field]: value,
    };

    // Pre-fill labels when field is enabled
    if (value) {
      const defaultLabels = getCompanyLabel(field);
      const currentLabels =
        settings.companyLabels?.[field as keyof typeof settings.companyLabels];

      // Only pre-fill if labels are not set (undefined/null) or empty strings
      const needsPreFill =
        !currentLabels ||
        (!currentLabels.primary && !currentLabels.secondary) ||
        (currentLabels.primary === "" && currentLabels.secondary === "");

      if (needsPreFill) {
        onChange({
          companyFields: newCompanyFields,
          companyLabels: {
            ...settings.companyLabels,
            [field]: {
              primary: defaultLabels.primary,
              secondary: defaultLabels.secondary,
            },
          },
        });
        return;
      }
    }

    onChange({
      companyFields: newCompanyFields,
    });
  };

  const updateCustomerFields = (field: string, value: boolean) => {
    const newCustomerFields = {
      ...settings.customerFields,
      [field]: value,
    };

    // Pre-fill labels when field is enabled
    if (value) {
      const defaultLabels = getCustomerLabel(field);
      const currentLabels =
        settings.customerLabels?.[
          field as keyof typeof settings.customerLabels
        ];

      // Only pre-fill if labels are not set (undefined/null) or empty strings
      const needsPreFill =
        !currentLabels ||
        (!currentLabels.primary && !currentLabels.secondary) ||
        (currentLabels.primary === "" && currentLabels.secondary === "");

      if (needsPreFill) {
        onChange({
          customerFields: newCustomerFields,
          customerLabels: {
            ...settings.customerLabels,
            [field]: {
              primary: defaultLabels.primary,
              secondary: defaultLabels.secondary,
            },
          },
        });
        return;
      }
    }

    onChange({
      customerFields: newCustomerFields,
    });
  };

  const updateCompanyLabel = (
    field: string,
    lang: "primary" | "secondary",
    value: string
  ) => {
    onChange({
      companyLabels: {
        ...settings.companyLabels,
        [field]: {
          ...settings.companyLabels?.[
            field as keyof typeof settings.companyLabels
          ],
          [lang]: value,
        },
      },
    });
  };

  const updateCustomerLabel = (
    field: string,
    lang: "primary" | "secondary",
    value: string
  ) => {
    onChange({
      customerLabels: {
        ...settings.customerLabels,
        [field]: {
          ...settings.customerLabels?.[
            field as keyof typeof settings.customerLabels
          ],
          [lang]: value,
        },
      },
    });
  };

  // Helper functions to get heading field names based on type
  // Only return the specific type's values, don't fall back to invoice
  const getHeadingPrimary = () => {
    if (templateType === 3) return settings.jobcardHeadingPrimary ?? "";
    if (templateType === 4) return settings.workorderHeadingPrimary ?? "";
    if (templateType === 6) return settings.deliveryNoteHeadingPrimary ?? "";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.proformaInvoiceHeadingPrimary ?? "";
    }
    return settings.invoiceHeadingPrimary ?? "";
  };

  const getHeadingSecondary = () => {
    if (templateType === 3) return settings.jobcardHeadingSecondary ?? "";
    if (templateType === 4) return settings.workorderHeadingSecondary ?? "";
    if (templateType === 6) return settings.deliveryNoteHeadingSecondary ?? "";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.proformaInvoiceHeadingSecondary ?? "";
    }
    return settings.invoiceHeadingSecondary ?? "";
  };

  const getHeadingLabel = () => {
    if (templateType === 3) return t("jobcardHeading") || "Jobcard Heading";
    if (templateType === 4) return t("workorderHeading") || "Workorder Heading";
    if (templateType === 6) return t("deliveryNoteHeading") || "Delivery Note Heading";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return t("proformaInvoiceHeading") || "Proforma Invoice Heading";
    }
    return t("invoiceHeading") || "Invoice Heading";
  };

  const handleHeadingPrimaryChange = (value: string) => {
    if (templateType === 3) {
      onChange({ jobcardHeadingPrimary: value });
    } else if (templateType === 4) {
      onChange({ workorderHeadingPrimary: value });
    } else if (templateType === 6) {
      onChange({ deliveryNoteHeadingPrimary: value });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ proformaInvoiceHeadingPrimary: value });
    } else {
      onChange({ invoiceHeadingPrimary: value });
    }
  };

  const handleHeadingSecondaryChange = (value: string) => {
    if (templateType === 3) {
      onChange({ jobcardHeadingSecondary: value });
    } else if (templateType === 4) {
      onChange({ workorderHeadingSecondary: value });
    } else if (templateType === 6) {
      onChange({ deliveryNoteHeadingSecondary: value });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ proformaInvoiceHeadingSecondary: value });
    } else {
      onChange({ invoiceHeadingSecondary: value });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Invoice/Jobcard/Workorder Heading */}
      <Box>
        <label
          className="font-Saira font-[600] text-[13px] mb-2 block"
          style={{ color: "#333" }}
        >
          {getHeadingLabel()}
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          <TextField
            size="small"
            label={primaryLanguageName}
            InputLabelProps={{ shrink: true }}
            placeholder={primaryLanguageName}
            title={primaryLanguageName}
            value={getHeadingPrimary()}
            onChange={(e) => handleHeadingPrimaryChange(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={secondaryLanguageName}
            InputLabelProps={{ shrink: true }}
            placeholder={secondaryLanguageName}
            title={secondaryLanguageName}
            value={getHeadingSecondary()}
            onChange={(e) => handleHeadingSecondaryChange(e.target.value)}
            fullWidth
          />
        </Box>
      </Box>

      {/* Show Sections */}
      <Box>
        <label
          className="font-Saira font-[600] text-[13px] mb-2 block"
          style={{ color: "#333" }}
        >
          {t("showSections")}
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.showCompanySection ?? true}
                onChange={(e) =>
                  onChange({ showCompanySection: e.target.checked })
                }
              />
            }
            label={t("companyDetails")}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.showCustomerSection ?? true}
                onChange={(e) =>
                  onChange({ showCustomerSection: e.target.checked })
                }
              />
            }
            label={t("customerDetails")}
            sx={{ m: 0 }}
          />
        </Box>
      </Box>

      {/* Company Fields */}
      {settings.showCompanySection && (
        <Box>
          <label
            className="font-Saira font-[600] text-[13px] mb-2 block"
            style={{ color: "#333" }}
          >
            {t("companyFields")}
          </label>
          <FormGroup sx={{ mt: 1 }}>
            {["name", "phone", "email", "address", "vat"].map((field) => (
              <Box key={field} sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        settings.companyFields?.[
                          field as keyof typeof settings.companyFields
                        ] ?? false
                      }
                      onChange={(e) =>
                        updateCompanyFields(field, e.target.checked)
                      }
                    />
                  }
                  label={t(
                    field === "name"
                      ? "companyName"
                      : field === "phone"
                        ? "phoneNo"
                        : field === "vat"
                          ? "vatNumber"
                          : field
                  )}
                  sx={{ m: 0 }}
                />
                {settings.companyFields?.[
                  field as keyof typeof settings.companyFields
                ] && (
                  <Box sx={{ ml: 4, mt: 1, display: "flex", gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder={(`${t(field === "name" ? "companyName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(field === "name" ? "companyName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={
                        settings.companyLabels?.[
                          field as keyof typeof settings.companyLabels
                        ]?.primary ?? ""
                      }
                      onChange={(e) =>
                        updateCompanyLabel(field, "primary", e.target.value)
                      }
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      placeholder={(`${t(field === "name" ? "companyName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(field === "name" ? "companyName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={
                        settings.companyLabels?.[
                          field as keyof typeof settings.companyLabels
                        ]?.secondary ?? ""
                      }
                      onChange={(e) =>
                        updateCompanyLabel(field, "secondary", e.target.value)
                      }
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </FormGroup>
        </Box>
      )}

      {/* Customer Fields */}
      {settings.showCustomerSection && (
        <Box>
          <label
            className="font-Saira font-[600] text-[13px] mb-2 block"
            style={{ color: "#333" }}
          >
            {t("customerFields")}
          </label>
          <FormGroup sx={{ mt: 1 }}>
            {["name", "phone", "email", "address", "vat"].map((field) => (
              <Box key={field} sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        settings.customerFields?.[
                          field as keyof typeof settings.customerFields
                        ] ?? false
                      }
                      onChange={(e) =>
                        updateCustomerFields(field, e.target.checked)
                      }
                    />
                  }
                  label={t(
                    field === "name"
                      ? "customerName"
                      : field === "phone"
                        ? "phoneNo"
                        : field === "vat"
                          ? "vatNumber"
                          : field
                  )}
                  sx={{ m: 0 }}
                />
                {settings.customerFields?.[
                  field as keyof typeof settings.customerFields
                ] && (
                  <Box sx={{ ml: 4, mt: 1, display: "flex", gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder={(`${t(field === "name" ? "customerName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(field === "name" ? "customerName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={
                        settings.customerLabels?.[
                          field as keyof typeof settings.customerLabels
                        ]?.primary ?? ""
                      }
                      onChange={(e) =>
                        updateCustomerLabel(field, "primary", e.target.value)
                      }
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      placeholder={(`${t(field === "name" ? "customerName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(field === "name" ? "customerName" : field === "phone" ? "phoneNo" : field === "vat" ? "vatNumber" : field)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={
                        settings.customerLabels?.[
                          field as keyof typeof settings.customerLabels
                        ]?.secondary ?? ""
                      }
                      onChange={(e) =>
                        updateCustomerLabel(field, "secondary", e.target.value)
                      }
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </FormGroup>
        </Box>
      )}
    </Box>
  );
};

export default HeaderSettingsPanel;
